import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useDialogFocus } from "@/hooks/useDialogFocus";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  hasProduct?: boolean;
  pending: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
};

export function QuotationItemEditor({
  open,
  mode,
  hasProduct,
  pending,
  onClose,
  onSubmit,
  children,
}: Props) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(
    dialogRef,
    () => {
      if (!pending) onClose();
    },
    { enabled: open, initialFocusRef: closeRef },
  );
  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="quotation-item-editor-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <section
        className="quotation-item-editor lnk-item-editor"
        role="dialog"
        aria-modal="true"
        aria-label={mode === "create" ? "Adicionar Item" : "Editar Item"}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header>
          <div>
            <h2>
              {mode === "create"
                ? hasProduct
                  ? "Adicionar produto"
                  : "Adicionar item"
                : "Editar item"}
            </h2>
          </div>
          <button
            type="button"
            className="lnk-modal-close"
            ref={closeRef}
            onClick={onClose}
            disabled={pending}
            aria-label="Fechar"
          >
            <X size={19} />
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!pending) onSubmit();
          }}
        >
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          <div className="quotation-item-editor-content" inert={pending}>
            {children}
          </div>
        </form>
      </section>
    </div>,
    document.body,
  );
}
